import {
  type ReactNode,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { salesEmailAdmins } from "@/Shared/Data/admins";
import { useCurrentUser } from "@freightsimple/hubble-api/useCurrentUser";
import { isAuthenticationDisabled } from "../../environment";
import type { SalesCallDraft } from "@/Features/ViewCompany/Helpers/viewCompanySalesCallHelpers";
import {
  CallContext,
  type CallData,
  type EssentialCompanyData,
} from "./CallContext";

const STORAGE_KEY = "HUBBLE_CALL_DATA";
const CHANNEL_NAME = "hubble-call-data";

type BroadcastMessage =
  | { type: "CALL_STARTED"; data: CallData }
  | { type: "CALL_UPDATED"; data: CallData }
  | { type: "CALL_ENDED" };

function shouldAutoAssign(
  temperature: string | undefined,
  currentAssignedTo: string | undefined,
  currentUserEmail: string | undefined,
): boolean {
  if (currentAssignedTo || !currentUserEmail) return false;

  const isEligible = salesEmailAdmins.some(
    (a) => a.emailAddress === currentUserEmail,
  );
  if (!isEligible) return false;

  return !temperature || (temperature !== "Dead" && temperature !== "Cold");
}

function buildDefaultDraft(
  company: EssentialCompanyData,
  assignedTo: string | undefined,
  overrides?: Partial<SalesCallDraft>,
): SalesCallDraft {
  return {
    salesCallType: "call",
    significant: true,
    notes: "",
    whatHappened: "",
    controlFreight: undefined,
    salesContactId: company.defaultSalesContactId,
    temperature: company.temperature,
    shipmentFrequency: company.shipmentFrequency,
    shipmentFrequencyNote: company.shipmentFrequencyNote ?? "",
    assignedTo,
    customerSuccessAssignedTo: company.customerSuccessAssignedTo,
    estimatedMonthlyFreightSpend:
      company.estimatedMonthlyFreightSpend !== undefined
        ? String(company.estimatedMonthlyFreightSpend)
        : "",
    estimatedMonthlyShipments:
      company.estimatedMonthlyShipments !== undefined
        ? String(company.estimatedMonthlyShipments)
        : "",
    captureEstimate:
      company.monthlyLtlCaptureEstimate !== undefined
        ? String(company.monthlyLtlCaptureEstimate * 100)
        : "",
    selectedLabels: company.companyLabelIds ?? [],
    ...overrides,
  };
}

export function CallProvider({ children }: { children: ReactNode }) {
  const [callData, setCallData] = useState<CallData | undefined>(undefined);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const channelRef = useRef<BroadcastChannel | undefined>(undefined);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const refreshCallbacksRef = useRef<Set<() => void>>(new Set());

  const user = useCurrentUser(isAuthenticationDisabled());
  const currentUserEmail = user?.email;

  // Persist to localStorage + broadcast
  const persist = useCallback(
    (data: CallData | undefined, message: BroadcastMessage) => {
      if (data) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
      channelRef.current?.postMessage(message);
    },
    [],
  );

  const triggerRefreshCallbacks = useCallback(() => {
    for (const cb of refreshCallbacksRef.current) {
      cb();
    }
  }, []);

  const endCall = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    localStorage.removeItem(STORAGE_KEY);
    channelRef.current?.postMessage({ type: "CALL_ENDED" } as BroadcastMessage);
    setCallData(undefined);
    setLogDialogOpen(false);
    triggerRefreshCallbacks();
  }, [triggerRefreshCallbacks]);

  const startCall = useCallback(
    (
      companyNoteId: string,
      company: EssentialCompanyData,
      initialDraft?: Partial<SalesCallDraft>,
    ) => {
      if (callData) {
        toast.error(
          "A call is already in progress. Please save or discard it first.",
        );
        return;
      }

      const assignedTo = shouldAutoAssign(
        company.temperature,
        company.assignedTo,
        currentUserEmail,
      )
        ? currentUserEmail
        : company.assignedTo;

      const data: CallData = {
        companyNoteId,
        company,
        draft: buildDefaultDraft(company, assignedTo, initialDraft),
        isSaving: false,
      };

      setCallData(data);
      persist(data, { type: "CALL_STARTED", data });
    },
    [callData, currentUserEmail, persist],
  );

  const updateDraft = useCallback(
    <K extends keyof SalesCallDraft>(field: K, value: SalesCallDraft[K]) => {
      setCallData((prev) => {
        if (!prev) return undefined;
        const updated: CallData = {
          ...prev,
          draft: { ...prev.draft, [field]: value },
        };
        persist(updated, { type: "CALL_UPDATED", data: updated });
        return updated;
      });
    },
    [persist],
  );

  const setSaving = useCallback(
    (saving: boolean) => {
      setCallData((prev) => {
        if (!prev) return undefined;
        const updated: CallData = { ...prev, isSaving: saving };
        persist(updated, { type: "CALL_UPDATED", data: updated });

        // Safety timeout: auto-end call after 5 seconds if saving hangs
        if (saving) {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(() => endCall(), 5_000);
        } else if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = undefined;
        }

        return updated;
      });
    },
    [persist, endCall],
  );

  const registerRefreshCallback = useCallback((cb: () => void) => {
    refreshCallbacksRef.current.add(cb);
  }, []);

  const unregisterRefreshCallback = useCallback((cb: () => void) => {
    refreshCallbacksRef.current.delete(cb);
  }, []);

  // Initialize from localStorage + set up BroadcastChannel
  useEffect(() => {
    // Restore persisted call data
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as CallData;
        if (parsed.companyNoteId && parsed.company && parsed.draft) {
          setCallData(parsed);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    }

    // Cross-tab sync
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;

    channel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
      const msg = event.data;
      switch (msg.type) {
        case "CALL_STARTED":
        case "CALL_UPDATED":
          setCallData(msg.data);
          break;
        case "CALL_ENDED":
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
          }
          localStorage.removeItem(STORAGE_KEY);
          setCallData(undefined);
          setLogDialogOpen(false);
          triggerRefreshCallbacks();
          break;
      }
    };

    return () => {
      channel.close();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [triggerRefreshCallbacks]);

  const contextValue = useMemo(
    () => ({
      callData,
      logDialogOpen,
      setLogDialogOpen,
      startCall,
      endCall,
      updateDraft,
      setSaving,
      registerRefreshCallback,
      unregisterRefreshCallback,
    }),
    [
      callData,
      logDialogOpen,
      startCall,
      endCall,
      updateDraft,
      setSaving,
      registerRefreshCallback,
      unregisterRefreshCallback,
    ],
  );

  return (
    <CallContext.Provider value={contextValue}>{children}</CallContext.Provider>
  );
}
